import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { useNavigate, useSearch, useRouter } from '@tanstack/react-router'
import { useTranslations } from '@/hooks/useTranslations'
import { useMemo, useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Columns2, LayoutGrid, Sheet, Save } from 'lucide-react'

import {
  emptyExtraCharge,
  emptyLineItem,
  extraChargeRowAmount,
  filterFilledExtraCharges,
  filterFilledLineItems,
  isLineItemFilled,
} from '@/components/pages/Admin/BillManagement/Form/billLineItemsShared'
import type {
  BillExtraChargeFormValue,
  BillLineItemFormValue,
} from '@/components/pages/Admin/BillManagement/Form/billLineItemsShared'
import type { BillFormViewSharedProps } from '@/components/pages/Admin/BillManagement/Form/billFormViewShared'
import { BillFormClassicView } from '@/components/pages/Admin/BillManagement/Form/BillFormClassicView'
import { BillFormExcelView } from '@/components/pages/Admin/BillManagement/Form/BillFormExcelView'
import { useBillFormViewMode } from '@/components/pages/Admin/BillManagement/Form/useBillFormViewMode'
import { Button, Form } from '@/components/ui'
import { toast } from '@/components/common/Toast'
import { useConfirmSaveModal, useUnsaveWarningModal } from '@/components/common/Modals'
import {
  fetchWholesaleCustomerProfile,
  useCreateBill,
  useUpdateBill,
} from '@/modules/bill/services/bill'
import { formatNumber } from '@/utils/number'
import { Bill } from '@/models/bill/bill'
import { useGetCustomerNoPage } from '@/modules/customer/services/customer'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  BILL_BUY_PARTY_VALUES,
  normalizeBillBuyPartyType,
  type BillBuyPartyType,
} from '@/constants/bill-buy-party'
import { BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useDefaultLocation } from '@/hooks/useDefaultLocation'
import { resolveRequiredLocation } from '@/utils/bill-location-preference'
import { cn } from '@/lib/utils'

dayjs.extend(utc)
dayjs.extend(timezone)
const BKK = 'Asia/Bangkok'

type BillFormProps = {
  action: 'create' | 'edit'
  tab?: string
  id?: string
  data?: Bill
  type?: 'pending' | 'all'
}

function billToLineItems(data?: Bill) {
  if (!data) return [emptyLineItem()]
  const raw = data as Bill & {
    line_items?: Array<{
      durian_type?: string | null
      grade?: string | null
      weight_kg?: number
      price_per_kg?: number
      description?: string | null
      box_count?: number | null
      weight_per_box_kg?: number | null
      strap_band?: string | null
      strap_quantity?: number | null
    }>
  }
  if (raw.line_items && raw.line_items.length > 0) {
    return raw.line_items.map((li) => ({
      durian_type: String(li.durian_type ?? '').trim(),
      grade: String(li.grade ?? '').trim(),
      weight_kg: Number(li.weight_kg) || 0,
      price_per_kg: Number(li.price_per_kg) || 0,
      description: li.description ?? '',
      box_count: Number(li.box_count) || 0,
      weight_per_box_kg: Number(li.weight_per_box_kg) || 0,
      strap_band: String(li.strap_band ?? '').trim(),
      strap_quantity: Number(li.strap_quantity) || 0,
    }))
  }
  return (data.order_information || []).map((order) => ({
    durian_type: order.durian_type || '',
    grade: (order as { grade?: string }).grade || '',
    weight_kg:
      order.cycles?.reduce((s, c) => s + (Number(c.product_quantity) || 0), 0) || 0,
    price_per_kg: Number(order.product_price) || 0,
    description: order.description || '',
  }))
}

function billToExtraCharges(data?: Bill) {
  const charges = data?.bill_information.extra_charges ?? []
  const mapped = charges
    .map((row) => {
      const name = String(row.expense_type_name ?? '').trim()
      const amount = Number(row.amount_baht) || 0
      const qty = row.quantity != null ? Number(row.quantity) : undefined
      const unitPrice = row.unit_price_baht != null ? Number(row.unit_price_baht) : undefined
      if (qty != null && unitPrice != null && qty >= 1 && unitPrice > 0) {
        return {
          expense_type_name: name,
          quantity: qty,
          unit_price_baht: unitPrice,
        }
      }
      if (amount > 0) {
        return {
          expense_type_name: name,
          quantity: 1,
          unit_price_baht: amount,
        }
      }
      if (name) {
        return {
          expense_type_name: name,
          quantity: qty != null && qty >= 1 ? qty : 0,
          unit_price_baht: unitPrice != null && unitPrice > 0 ? unitPrice : 0,
        }
      }
      return null
    })
    .filter((row): row is ReturnType<typeof emptyExtraCharge> => row != null)
  if (mapped.length) return mapped

  const legacy = [
    {
      expense_type_name: 'ค่ากล่อง',
      amount_baht: Number(data?.bill_information.box_fee_baht ?? 0),
    },
    {
      expense_type_name: 'สติกเกอร์',
      amount_baht: Number(data?.bill_information.sticker_fee_baht ?? 0),
    },
  ]
    .filter((row) => row.amount_baht > 0)
    .map((row) => ({
      expense_type_name: row.expense_type_name,
      quantity: 1,
      unit_price_baht: row.amount_baht,
    }))
  if (legacy.length) return legacy

  return [emptyExtraCharge()]
}

function billToEditFormValues(data: Bill) {
  return {
    bill_date: (data as Bill & { bill_date?: string })?.bill_date || dayjs().tz(BKK).format('YYYY-MM-DD'),
    location: ((data as Bill & { location?: string })?.location as BillLocationValue) || 'chanthaburi',
    bill_information: {
      bill_type: data?.bill_information.bill_type || 'buy',
      sale_mode: data?.bill_information.sale_mode === 'wholesale' ? 'wholesale' : 'retail',
      buy_party_type:
        data?.bill_information.bill_type === 'buy'
          ? normalizeBillBuyPartyType(data?.bill_information.buy_party_type)
          : undefined,
      container_no: data?.bill_information.container_no ?? '',
      customer_sequence_no: data?.bill_information.customer_sequence_no ?? '',
      discount_baht: Number(data?.bill_information.discount_baht ?? 0),
      extra_charges: billToExtraCharges(data),
    },
    customer_information: {
      name: data?.customer_information.name || '',
      phone: data?.customer_information.phone || '',
      car_registration: data?.customer_information.car_registration || '',
      address: (data?.customer_information as { address?: string }).address ?? '',
      tax_id: (data?.customer_information as { tax_id?: string }).tax_id ?? '',
      brand_mark: (data?.customer_information as { brand_mark?: string }).brand_mark ?? '',
    },
    line_items: billToLineItems(data),
    payment_information: {
      payment_method: data?.payment_information.payment_method || '',
      payment_status: data?.payment_information.payment_status || '',
    },
  }
}

export const BillForm = ({ action, tab = 'buy', data, id, type = 'all' }: BillFormProps) => {
  const isCreate = action === 'create'
  const { defaultLocation, isLoading: isDefaultLocationLoading } = useDefaultLocation()
  const initialLocation = useMemo(
    () => resolveRequiredLocation(defaultLocation || undefined),
    [defaultLocation],
  )
  const search = useSearch({ strict: false }) as { tab?: string; q?: string }
  const currentTab = search.tab || tab
  const currentQuery = search.q || ''

  const { ConfirmSaveModal, showConfirmSaveModal } = useConfirmSaveModal()
  const { UnsaveWarningModal, showUnsaveWarningModal } = useUnsaveWarningModal()

  const t = useTranslations('admin.bill_management.form')
  const navigate = useNavigate()
  const router = useRouter()

  const { data: customerListNoPage } = useGetCustomerNoPage()
  const $apiCreateBill = useCreateBill()
  const $apiUpdateBill = useUpdateBill(id ?? '')

  const formSchema = z
    .object({
      bill_date: z.string().min(1, 'เลือกวันที่บิล'),
      location: z.enum(BILL_LOCATION_VALUES),
      bill_information: z.object({
        bill_type: z.string().nonempty('กรุณากรอกข้อมูลของรายการให้ครบถ้วน'),
        sale_mode: z.enum(['retail', 'wholesale']).optional(),
        buy_party_type: z.enum(BILL_BUY_PARTY_VALUES).optional(),
        container_no: z.string().optional(),
        customer_sequence_no: z.string().optional(),
        discount_baht: z.coerce.number().min(0).optional(),
        extra_charges: z
          .array(
            z.object({
              expense_type_name: z.string().optional(),
              quantity: z.coerce.number().min(0).optional(),
              unit_price_baht: z.coerce.number().min(0).optional(),
            }),
          )
          .optional(),
      }),
      customer_information: z.object({
        name: z.string().min(1, 'กรุณากรอกข้อมูลของรายการให้ครบถ้วน'),
        phone: z.string().optional(),
        car_registration: z.string().optional(),
        address: z.string().optional(),
        tax_id: z.string().optional(),
        brand_mark: z.string().optional(),
      }),
      line_items: z
        .array(
          z.object({
            durian_type: z.string().optional(),
            grade: z.string().optional(),
            weight_kg: z.coerce.number().optional(),
            price_per_kg: z.coerce.number().optional(),
            description: z.string().optional(),
            box_count: z.coerce.number().optional(),
            weight_per_box_kg: z.coerce.number().optional(),
            strap_band: z.string().optional(),
            strap_quantity: z.coerce.number().optional(),
          }),
        )
        .min(1, 'ต้องมีอย่างน้อย 1 รายการ'),
      payment_information: z.object({
        payment_method: z.string().min(1, 'กรุณากรอกข้อมูลของรายการให้ครบถ้วน'),
        payment_status: z.string().min(1, 'กรุณากรอกข้อมูลของรายการให้ครบถ้วน'),
        payment_slip: z.instanceof(File).optional(),
      }),
    })
    .superRefine((data, ctx) => {
      if (data.bill_information.bill_type === 'buy' && !data.bill_information.buy_party_type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'เลือกว่าเป็นหน้าแผงหรือสวน',
          path: ['bill_information', 'buy_party_type'],
        })
      }

      const filledLineItems = data.line_items.filter(isLineItemFilled)
      if (filledLineItems.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ต้องมีอย่างน้อย 1 รายการ',
          path: ['line_items'],
        })
      }

      data.line_items.forEach((li, i) => {
        if (!isLineItemFilled(li)) return
        if (Number(li.weight_kg) < 0.001) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'กรอกน้ำหนัก (กก.)',
            path: ['line_items', i, 'weight_kg'],
          })
        }
        if (Number(li.price_per_kg) < 0.01) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'กรอกราคา/กก.',
            path: ['line_items', i, 'price_per_kg'],
          })
        }
      })

      const wholesale = data.bill_information.bill_type === 'sell' && data.bill_information.sale_mode === 'wholesale'
      if (!wholesale) return
      if (!data.bill_information.container_no?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'กรุณากรอก Cont. NO.',
          path: ['bill_information', 'container_no'],
        })
      }
      const chargeRows = data.bill_information.extra_charges ?? []
      const seen = new Set<string>()
      chargeRows.forEach((row, i) => {
        const name = row.expense_type_name?.trim() ?? ''
        const qty = Number(row.quantity ?? 0)
        const price = Number(row.unit_price_baht ?? 0)
        if (name) {
          if (qty < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'กรอกจำนวน',
              path: ['bill_information', 'extra_charges', i, 'quantity'],
            })
          }
          if (price <= 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'กรอกราคาต่อชิ้น',
              path: ['bill_information', 'extra_charges', i, 'unit_price_baht'],
            })
          }
          const key = name.toLowerCase()
          if (seen.has(key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'ห้ามเลือกประเภทค่าใช้จ่ายซ้ำ',
              path: ['bill_information', 'extra_charges', i, 'expense_type_name'],
            })
          } else {
            seen.add(key)
          }
        }
      })
      data.line_items.forEach((li, i) => {
        if (!isLineItemFilled(li)) return
        const boxes = Number(li.box_count)
        const per = Number(li.weight_per_box_kg)
        if (!boxes || boxes < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'กรอกจำนวนกล่อง',
            path: ['line_items', i, 'box_count'],
          })
        }
        if (!per || per <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'กรอกน้ำหนักต่อกล่อง',
            path: ['line_items', i, 'weight_per_box_kg'],
          })
        }
        const expected = Math.round(boxes * per * 1000) / 1000
        if (boxes >= 1 && per > 0 && Math.abs(expected - Number(li.weight_kg)) > 0.02) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'น้ำหนักรวมต้องเท่ากับ กล่อง × กก./กล่อง',
            path: ['line_items', i, 'weight_kg'],
          })
        }
        const strapBand = li.strap_band?.trim()
        const strapQty = Number(li.strap_quantity)
        if (strapBand && (!strapQty || strapQty < 1)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'กรอกจำนวนสายรัด',
            path: ['line_items', i, 'strap_quantity'],
          })
        }
      })
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues:
      isCreate && !data
        ? {
            bill_date: dayjs().tz(BKK).format('YYYY-MM-DD'),
            location: initialLocation,
            bill_information: {
              bill_type: currentTab,
              sale_mode: 'retail',
              buy_party_type: currentTab === 'buy' ? 'stall' : undefined,
              container_no: '',
              customer_sequence_no: '',
              discount_baht: 0,
              extra_charges: [emptyExtraCharge()],
            },
            customer_information: {
              name: '',
              phone: '',
              car_registration: '',
              address: '',
              tax_id: '',
              brand_mark: '',
            },
            line_items: [emptyLineItem()],
            payment_information: { payment_method: '', payment_status: '' },
          }
        : billToEditFormValues(data!),
  })

  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'line_items' })
  const {
    fields: extraChargeFields,
    append: appendExtraCharge,
    remove: removeExtraCharge,
  } = useFieldArray({ control: form.control, name: 'bill_information.extra_charges' })

  const hydratedBillIdRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    if (action !== 'edit' || !data?.id) return
    if (hydratedBillIdRef.current === data.id) return
    hydratedBillIdRef.current = data.id
    form.reset(billToEditFormValues(data))
  }, [action, data, form])

  const { watch, setValue, getValues, formState: { errors, isDirty } } = form

  useEffect(() => {
    if (!isCreate || data || isDefaultLocationLoading) return
    setValue('location', initialLocation, { shouldValidate: false })
  }, [isCreate, data, isDefaultLocationLoading, initialLocation, setValue])

  const isLoading = false

  const billTypeWatch = watch('bill_information.bill_type')
  const saleModeWatch = watch('bill_information.sale_mode')
  const buyPartyTypeWatch = watch('bill_information.buy_party_type') as BillBuyPartyType | undefined
  const isBuyBill = billTypeWatch === 'buy'
  const isWholesaleSell = billTypeWatch === 'sell' && saleModeWatch === 'wholesale'
  const buyPartyForLabels = normalizeBillBuyPartyType(buyPartyTypeWatch)

  const syncWholesaleRowWeight = useCallback(
    (rowIndex: number) => {
      if (getValues('bill_information.sale_mode') !== 'wholesale') return
      const boxes = Number(getValues(`line_items.${rowIndex}.box_count`)) || 0
      const per = Number(getValues(`line_items.${rowIndex}.weight_per_box_kg`)) || 0
      if (boxes > 0 && per > 0) {
        setValue(`line_items.${rowIndex}.weight_kg`, Math.round(boxes * per * 1000) / 1000, {
          shouldValidate: true,
          shouldDirty: true,
        })
      }
    },
    [getValues, setValue],
  )

  const createBill = async () => {
    const d = watch()
    const wholesaleSell = d.bill_information.bill_type === 'sell' && d.bill_information.sale_mode === 'wholesale'
    const line_items = filterFilledLineItems(d.line_items).map((li) => ({
      durian_type: li.durian_type || undefined,
      grade: li.grade || undefined,
      weight_kg: Number(li.weight_kg),
      price_per_kg: Number(li.price_per_kg),
      description: li.description || undefined,
      ...(wholesaleSell
        ? {
            box_count: Math.round(Number(li.box_count)),
            weight_per_box_kg: Number(li.weight_per_box_kg),
            strap_band: li.strap_band?.trim() || undefined,
            strap_quantity: li.strap_band?.trim()
              ? Math.round(Number(li.strap_quantity))
              : undefined,
          }
        : {}),
    }))

    try {
      const { payment_slip: _slip, ...paymentRest } = d.payment_information
      const payload = {
        bill_date: d.bill_date,
        location: d.location,
        customer_information: {
          ...d.customer_information,
          address: d.customer_information.address || undefined,
          tax_id: d.customer_information.tax_id || undefined,
          brand_mark: d.customer_information.brand_mark || undefined,
        },
        line_items,
        payment_information: paymentRest,
        bill_information: {
          bill_type: d.bill_information.bill_type,
          sale_mode: d.bill_information.bill_type === 'sell' ? (d.bill_information.sale_mode ?? 'retail') : 'retail',
          ...(d.bill_information.bill_type === 'buy'
            ? { buy_party_type: normalizeBillBuyPartyType(d.bill_information.buy_party_type) }
            : {}),
          ...(wholesaleSell
            ? {
                container_no: d.bill_information.container_no?.trim() || undefined,
                discount_baht: Number(d.bill_information.discount_baht ?? 0),
                extra_charges: filterFilledExtraCharges(d.bill_information.extra_charges ?? [])
                  .map((row) => ({
                    expense_type_name: row.expense_type_name?.trim() || undefined,
                    quantity: Math.round(Number(row.quantity)),
                    unit_price_baht: Number(row.unit_price_baht),
                  }))
                  .filter(
                    (row) =>
                      row.expense_type_name &&
                      row.quantity >= 1 &&
                      row.unit_price_baht > 0,
                  ),
              }
            : {}),
        },
      }
      await $apiCreateBill.mutateAsync(payload as any)
      toast.toastSuccess('สร้างบิลสำเร็จ')
      router.navigate({ to: `/admin/bill`, search: { tab: currentTab } })
    } catch (e) {
      const msg =
        axios.isAxiosError(e) && e.response?.data
          ? (() => {
              const d0 = e.response?.data as { message?: string | string[] }
              if (Array.isArray(d0?.message)) return d0.message.join(', ')
              if (typeof d0?.message === 'string') return d0.message
              return e.message
            })()
          : e instanceof Error
            ? e.message
            : 'สร้างบิลไม่สำเร็จ'
      toast.toastFailed(msg)
    }
  }

  const updateBill = async () => {
    const d = watch()
    const wholesaleSell = d.bill_information.bill_type === 'sell' && d.bill_information.sale_mode === 'wholesale'
    const line_items = filterFilledLineItems(d.line_items).map((li) => ({
      durian_type: li.durian_type || undefined,
      grade: li.grade || undefined,
      weight_kg: Number(li.weight_kg),
      price_per_kg: Number(li.price_per_kg),
      description: li.description || undefined,
      ...(wholesaleSell
        ? {
            box_count: Math.round(Number(li.box_count)),
            weight_per_box_kg: Number(li.weight_per_box_kg),
            strap_band: li.strap_band?.trim() || undefined,
            strap_quantity: li.strap_band?.trim()
              ? Math.round(Number(li.strap_quantity))
              : undefined,
          }
        : {}),
    }))

    try {
      const { payment_slip: _slip, ...paymentRest } = d.payment_information
      const payload = {
        bill_date: d.bill_date,
        location: d.location,
        customer_information: {
          ...d.customer_information,
          address: d.customer_information.address || undefined,
          tax_id: d.customer_information.tax_id || undefined,
          brand_mark: d.customer_information.brand_mark || undefined,
        },
        line_items,
        payment_information: paymentRest,
        bill_information: {
          bill_type: d.bill_information.bill_type,
          sale_mode: d.bill_information.bill_type === 'sell' ? (d.bill_information.sale_mode ?? 'retail') : 'retail',
          ...(d.bill_information.bill_type === 'buy'
            ? { buy_party_type: normalizeBillBuyPartyType(d.bill_information.buy_party_type) }
            : {}),
          ...(wholesaleSell
            ? {
                container_no: d.bill_information.container_no?.trim() || undefined,
                discount_baht: Number(d.bill_information.discount_baht ?? 0),
                extra_charges: filterFilledExtraCharges(d.bill_information.extra_charges ?? [])
                  .map((row) => ({
                    expense_type_name: row.expense_type_name?.trim() || undefined,
                    quantity: Math.round(Number(row.quantity)),
                    unit_price_baht: Number(row.unit_price_baht),
                  }))
                  .filter(
                    (row) =>
                      row.expense_type_name &&
                      row.quantity >= 1 &&
                      row.unit_price_baht > 0,
                  ),
              }
            : {}),
        },
      }
      await $apiUpdateBill.mutateAsync(payload as any)
      toast.toastSuccess('แก้ไขบิลสำเร็จ')
      router.navigate({
        to: type === 'pending' ? `/admin/outstanding` : `/admin/bill`,
        search:
          type === 'pending'
            ? { tab: currentTab, q: currentQuery || undefined }
            : { tab: currentTab },
      })
    } catch (e) {
      const msg =
        axios.isAxiosError(e) && e.response?.data
          ? (() => {
              const d0 = e.response?.data as { message?: string | string[] }
              if (Array.isArray(d0?.message)) return d0.message.join(', ')
              if (typeof d0?.message === 'string') return d0.message
              return e.message
            })()
          : e instanceof Error
            ? e.message
            : 'แก้ไขบิลไม่สำเร็จ'
      toast.toastFailed(msg)
    }
  }

  const handleFormSubmit = async () => {
    if (action === 'create') await createBill()
    else await updateBill()
  }

  const customerOptions = useMemo(() => {
    const raw = customerListNoPage?.data
    const list = Array.isArray(raw) ? raw : []
    return list.map((customer: { name: string; id: string }) => ({
      label: customer?.name,
      value: customer.id,
    }))
  }, [customerListNoPage])

  const customerNameSuggestions = useMemo(
    () => customerOptions.map((o) => o.label).filter((label): label is string => Boolean(label)),
    [customerOptions],
  )

  const customerNameWatch = watch('customer_information.name')

  const [profileLoading, setProfileLoading] = useState(false)

  const applyCustomerProfile = useCallback(
    async (name: string) => {
      const trimmed = name.trim()
      if (!trimmed) return
      const known = customerNameSuggestions.some(
        (n) => n.toLowerCase() === trimmed.toLowerCase(),
      )
      if (!known) return
      setProfileLoading(true)
      try {
        const profile = await fetchWholesaleCustomerProfile(trimmed)
        if (!profile) return
        setValue('customer_information.phone', profile.phone, { shouldDirty: true })
        setValue('customer_information.car_registration', profile.car_registration, {
          shouldDirty: true,
        })
        if (getValues('bill_information.sale_mode') === 'wholesale') {
          setValue('customer_information.address', profile.address, { shouldDirty: true })
          setValue('customer_information.tax_id', profile.tax_id, { shouldDirty: true })
          setValue('customer_information.brand_mark', profile.brand_mark, { shouldDirty: true })
        }
      } finally {
        setProfileLoading(false)
      }
    },
    [customerNameSuggestions, getValues, setValue],
  )

  const lineItemsWatch = watch('line_items')
  const filledLineItemsWatch = useMemo(
    () => filterFilledLineItems(lineItemsWatch ?? []),
    [lineItemsWatch],
  )
  const totalKg = filledLineItemsWatch.reduce((a, li) => a + (Number(li.weight_kg) || 0), 0)
  const subtotalBaht = filledLineItemsWatch.reduce(
    (a, li) => a + (Number(li.weight_kg) || 0) * (Number(li.price_per_kg) || 0),
    0,
  )
  const discountWatch = Number(watch('bill_information.discount_baht') ?? 0)
  const extraChargeWatch = watch('bill_information.extra_charges') ?? []
  const extraChargeTotal = extraChargeWatch.reduce(
    (sum, row) => sum + extraChargeRowAmount(row),
    0,
  )
  const isWholesaleSellSummary =
    watch('bill_information.bill_type') === 'sell' && watch('bill_information.sale_mode') === 'wholesale'
  const netBaht = isWholesaleSellSummary
    ? Math.max(0, Math.round((subtotalBaht + extraChargeTotal - discountWatch) * 100) / 100)
    : subtotalBaht

  const { viewMode, setViewMode } = useBillFormViewMode()

  const onBillTypeChange = useCallback(
    (value: string) => {
      if (isCreate) {
        router.navigate({ to: `/admin/bill/create`, search: { tab: value } })
      }
    },
    [isCreate, router],
  )

  const lineItemsErrorMessage =
    typeof errors.line_items?.message === 'string' ? errors.line_items.message : undefined
  const hasLineItemsError = Boolean(
    typeof errors.line_items === 'object' &&
      errors.line_items !== null &&
      'message' in errors.line_items &&
      typeof (errors.line_items as { message?: string }).message === 'string',
  )

  const viewProps: BillFormViewSharedProps = {
    control: form.control,
    isCreate,
    isLoading,
    isBuyBill,
    isWholesaleSell,
    billTypeWatch: billTypeWatch ?? '',
    buyPartyForLabels,
    onBillTypeChange,
    lineItemFields: fields,
    appendLineItem: append,
    removeLineItem: remove,
    lineItems: lineItemsWatch as BillLineItemFormValue[] | undefined,
    syncWholesaleRowWeight,
    extraChargeFields,
    appendExtraCharge,
    removeExtraCharge,
    extraCharges: extraChargeWatch as BillExtraChargeFormValue[] | undefined,
    customerNameSuggestions,
    profileLoading,
    applyCustomerProfile,
    customerNameWatch: customerNameWatch ?? '',
    totalKg,
    subtotalBaht,
    extraChargeTotal,
    discountWatch,
    isWholesaleSellSummary,
    netBaht,
    lineItemsErrorMessage,
    hasLineItemsError,
  }

  return (
    <Form {...form}>
      <form
        className="w-full max-w-full space-y-0 pb-24 text-sm"
        onSubmit={form.handleSubmit(showConfirmSaveModal)}
      >
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">เลือกโหมดการกรอกข้อมูล</p>
          <div
            className="inline-flex rounded-lg border border-border/80 bg-muted/30 p-0.5"
            role="group"
            aria-label="โหมดฟอร์มบิล"
          >
            <button
              type="button"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
                viewMode === 'classic'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setViewMode('classic')}
              aria-pressed={viewMode === 'classic'}
            >
              <LayoutGrid className="size-3.5 shrink-0" aria-hidden />
              ฟอร์มปกติ
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
                viewMode === 'hybrid'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setViewMode('hybrid')}
              aria-pressed={viewMode === 'hybrid'}
            >
              <Columns2 className="size-3.5 shrink-0" aria-hidden />
              Hybrid
            </button>
            <button
              type="button"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
                viewMode === 'excel'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => setViewMode('excel')}
              aria-pressed={viewMode === 'excel'}
            >
              <Sheet className="size-3.5 shrink-0" aria-hidden />
              โหมด Excel
            </button>
          </div>
        </div>

        {viewMode === 'excel' ? (
          <BillFormExcelView {...viewProps} />
        ) : (
          <BillFormClassicView
            {...viewProps}
            gridVariant={viewMode === 'hybrid' ? 'excel' : 'default'}
          />
        )}

        <div className="sticky bottom-0 z-20 mt-8 rounded-xl border border-border bg-card/95 p-4 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.12)] backdrop-blur-sm supports-[backdrop-filter]:bg-card/90 md:p-5">
          <div className="mb-4 flex flex-col gap-1 border-b border-border pb-4 text-sm sm:hidden">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">สรุปก่อนบันทึก</span>
            <span className="tabular-nums text-foreground">
              น้ำหนัก <strong>{formatNumber(totalKg)}</strong> กก. · ยอดสุทธิ <strong>{formatNumber(netBaht)}</strong> บาท
            </span>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="hidden text-sm text-muted-foreground sm:block md:text-base">
              <span className="font-semibold text-foreground">สรุป: </span>
              <span className="tabular-nums">
                น้ำหนัก {formatNumber(totalKg)} กก. · ยอดสุทธิ {formatNumber(netBaht)} บาท
              </span>
            </div>
            <div className="flex w-full flex-col-reverse gap-3 sm:w-auto sm:flex-row sm:justify-end sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                disabled={isLoading}
                className="h-9 text-sm text-muted-foreground hover:text-foreground sm:min-w-[6rem]"
                onClick={() => showUnsaveWarningModal()}
              >
                {t('button.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !isDirty}
                className="h-9 min-w-[9rem] gap-2 text-sm font-semibold shadow-sm sm:min-w-[10rem]"
              >
                <Save className="size-4 shrink-0" aria-hidden />
                {t('button.save')}
              </Button>
            </div>
          </div>
        </div>

        <UnsaveWarningModal
          title="ยืนยันการออกจากหน้านี้"
          description="ข้อมูลที่คุณกรอกจะไม่ถูกบันทึก"
          onDiscard={() =>
            navigate({
              to: type === 'pending' ? `/admin/outstanding` : `/admin/bill`,
              search:
                type === 'pending'
                  ? { tab: currentTab, q: currentQuery || undefined }
                  : { tab: currentTab },
            })
          }
        />
        <ConfirmSaveModal
          title="ยืนยันการบันทึก"
          description="คุณต้องการบันทึกข้อมูลนี้หรือไม่"
          onConfirm={() => handleFormSubmit()}
        />
      </form>
    </Form>
  )
}
